import { authToken } from "@infrastructure/clients/http/authToken";

export interface SubscriptionOptions {
  onMessage: (data: any) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  reconnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

export abstract class WebSocketClient {
  abstract wsBaseUrl: string;
  protected ws: WebSocket | null = null;
  private subscriptions = new Map<string, Set<(data: any) => void>>();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private currentEndpoint: string | null = null;

  async connect(endpoint: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const wsUrl = endpoint.startsWith("ws")
        ? endpoint
        : `${this.wsBaseUrl.replace(/^https?/, "ws")}${endpoint}`;

      this.currentEndpoint = endpoint;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log("[WebSocket] Connected to", endpoint);
        this.reconnectAttempts = 0;
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error("[WebSocket] Failed to parse message:", error);
        }
      };

      this.ws.onerror = (error) => {
        console.error("[WebSocket] Error:", error);
        reject(error);
      };

      this.ws.onclose = () => {
        console.log("[WebSocket] Disconnected from", endpoint);
        this.ws = null;
        if (this.currentEndpoint) {
          this.handleReconnect(this.currentEndpoint);
        }
      };
    });
  }

  subscribe(channel: string, options: SubscriptionOptions): () => void {
    if (!this.subscriptions.has(channel)) {
      this.subscriptions.set(channel, new Set());
    }

    const handler = (data: any) => {
      try {
        options.onMessage(data);
      } catch (error) {
        options.onError?.(error as Error);
      }
    };

    this.subscriptions.get(channel)!.add(handler);

    this.send({
      type: "subscribe",
      channel,
    });

    return () => {
      this.subscriptions.get(channel)?.delete(handler);
      if (this.subscriptions.get(channel)?.size === 0) {
        this.subscriptions.delete(channel);
        this.send({
          type: "unsubscribe",
          channel,
        });
      }
    };
  }

  send(data: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn("[WebSocket] Cannot send message: connection not open");
    }
  }

  private handleMessage(data: any): void {
    if (data.channel && this.subscriptions.has(data.channel)) {
      this.subscriptions.get(data.channel)!.forEach((handler) => handler(data.payload || data));
    } else if (!data.channel) {
      // Если нет channel, отправляем всем подписчикам
      this.subscriptions.forEach((handlers) => {
        handlers.forEach((handler) => handler(data));
      });
    }
  }

  private handleReconnect(endpoint: string): void {
    const maxAttempts = 5;
    if (this.reconnectAttempts < maxAttempts) {
      this.reconnectAttempts++;
      const delay = 1000 * this.reconnectAttempts;
      console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${maxAttempts})`);
      this.reconnectTimer = setTimeout(() => {
        this.connect(endpoint).catch(console.error);
      }, delay);
    } else {
      console.error("[WebSocket] Max reconnection attempts reached");
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.subscriptions.clear();
    this.reconnectAttempts = 0;
    this.currentEndpoint = null;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

